import type { Schemas } from '#shopware'
import { computed, onMounted, onUnmounted, type Ref } from 'vue'
import { useState, useNuxtApp } from '#imports'
import { resolvePromotionClickTarget } from '../utils/promotionClickTarget'

export type PromotionData = Schemas['PromotionData']

export function usePromotionStore() {
    const promotionData = useState<PromotionData | null>('promotion', () => null)

    function setPromotionData(data: PromotionData) {
        promotionData.value = data
    }

    return {
        promotionData,
        setPromotionData
    }
}

const isEmpty = (value: string | null | undefined): boolean => {
    return value == null || value.trim() === ''
}

export function usePromotionObserver(targetElementRef?: Ref<HTMLElement | null>, promotionData?: PromotionData | null, index?: number) {
    const { setPromotionData } = usePromotionStore()
    const nuxtApp = useNuxtApp()
    let observer: IntersectionObserver | null = null

    const viewPromotion = (promotionData: PromotionData): void => {
        nuxtApp.callHook('viewPromotion', { payload: promotionData })
        if (observer) {
            observer.disconnect()
            observer = null
        }
    }

    const selectPromotion = (promotionData: PromotionData, clickSource?: MouseEvent | HTMLElement | null): void => {
        if (promotionData == null) return

        setPromotionData(promotionData)
        const click = resolvePromotionClickTarget(clickSource)
        nuxtApp.callHook('selectPromotion', { payload: promotionData, ...(click ?? {}) })
    }

    const computedPromotionData = computed(() => {
        if (
            !promotionData ||
            (
                isEmpty(promotionData.promotion_name) &&
                isEmpty(promotionData.promotion_id) &&
                isEmpty(promotionData.creative_name) &&
                isEmpty(promotionData.creative_slot)
            )
        ) {
            return
        }

        return {
            ...promotionData,
            creative_slot: `${promotionData.creative_slot}${index != null ? index : ''}`
        }
    })

    onMounted(() => {
        if (targetElementRef?.value && promotionData) {
            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (computedPromotionData?.value != null) viewPromotion(computedPromotionData.value)
                    }
                })
            })

            observer.observe(targetElementRef.value)
        }
    })

    onUnmounted(() => {
        if (observer) {
            observer.disconnect()
        }
    })

    return {
        viewPromotion,
        selectPromotion,
        computedPromotionData
    }
}
